// Overtime Policy — string constants and enum option arrays

export const overtimeLabel = {
  // Page
  pageTitle: "Overtime Policy",
  pageSubtitle: "Configure overtime rules, thresholds and multipliers for your organisation",

  // Section headings
  basicInfo: "Basic Information",
  thresholds: "Thresholds & Limits",
  calculation: "Calculation Settings",
  approval: "Approval & Automation",
  compensation: "Compensation Settings",

  // Field labels
  policyName: "Policy Name",
  description: "Description",
  minOtThreshold: "Minimum OT Threshold (hours)",
  maxOtPerDay: "Max OT Per Day (hours)",
  weeklyOtLimit: "Weekly OT Limit (hours)",
  calculationType: "Calculation Type",
  approvalType: "Request Type",
  roundingType: "Rounding Type",
  weekendMultiplier: "Weekend Multiplier",
  holidayMultiplier: "Holiday Multiplier",
  compoffRate: "Comp-Off Rate",
  compoffRateDesc:
    "Leave hours credited per overtime hour worked (e.g. 1.5 = 1.5h leave per 1h OT)",
  autoOt: "Enable Auto OT",
  requiresApproval: "Requires Approval",
  isActive: "Active",

  // Actions
  addPolicy: "Add Policy",
  editPolicy: "Edit Policy",
  deactivatePolicy: "Deactivate Policy",
  activatePolicy: "Activate Policy",
  viewPolicy: "View Policy",

  // Confirmation
  confirmToggleTitle: "Confirm Status Change",
  confirmToggleBody: (name, action) => `Are you sure you want to ${action} the policy "${name}"?`,

  // Empty / info
  noPolicies: "No overtime policies found",
  noPoliciesDesc: "Create your first overtime policy to get started",
  howItWorks: "How overtime policies work",
  howItWorksDescPayment:
    "This organisation uses Overtime Payment. Overtime hours are compensated monetarily using the configured multipliers for regular, weekend, and holiday work.",
  howItWorksDescCompoff:
    "This organisation uses Comp-Off Leave Credit. Overtime hours are converted into compensatory leave credits at the configured rate instead of monetary payment.",
  howItWorksDesc:
    "Overtime policies define the rules for calculating and approving overtime. The policy controls thresholds, multipliers, rounding and whether overtime is processed automatically or requires manual approval.",

  // Compensation type badge labels
  compensationPayment: "Overtime Payment",
  compensationCompoff: "Comp-Off Leave",

  // Toast / validation
  autoOrApproval: "At least one of Auto OT or Requires Approval must be enabled.",
};

// ── Enum option arrays ──────────────────────────────────────────────────────

export const calculationTypeOptions = [
  {
    value: "After Shift Hours",
    label: "After Shift Hours",
    description: "OT calculated after the standard shift ends",
  },
  {
    value: "After Threshold",
    label: "After Threshold",
    description: "OT calculated after the minimum threshold is met",
  },
];

export const approvalTypeOptions = [
  {
    value: "Post Overtime Required",
    label: "Post Overtime Required",
    description: "Manager approves after overtime is worked",
  },
  {
    value: "Pre Overtime Required",
    label: "Pre Overtime Required",
    description: "Approval must be obtained before working OT",
  },
];

export const roundingTypeOptions = [
  {
    value: "Exact Minutes",
    label: "Exact Minutes",
    description: "No rounding — use exact minutes worked",
  },
  {
    value: "Nearest 15 Minutes",
    label: "Nearest 15 Minutes",
    description: "Round to nearest 15-minute block",
  },
  {
    value: "Nearest 30 Minutes",
    label: "Nearest 30 Minutes",
    description: "Round to nearest 30-minute block",
  },
  {
    value: "Full Hour Only",
    label: "Full Hour Only",
    description: "Round down to the nearest full hour",
  },
];

export const multiplierOptions = [
  { value: 1, label: "1.0×" },
  { value: 1.25, label: "1.25×" },
  { value: 1.5, label: "1.5×" },
  { value: 1.75, label: "1.75×" },
  { value: 2, label: "2.0×" },
  { value: 2.5, label: "2.5×" },
  { value: 3, label: "3.0×" },
];

export const compoffRateOptions = [
  { value: 1, label: "1.0× (1h leave per 1h OT)" },
  { value: 1.25, label: "1.25× (1.25h leave per 1h OT)" },
  { value: 1.5, label: "1.5× (1.5h leave per 1h OT)" },
  { value: 2, label: "2.0× (2h leave per 1h OT)" },
];

// ── Compensation type constants (mirrors Organization)
export const OT_COMP_TYPE = {
  PAYMENT: "overtime_payment",
  COMPOFF: "compoff_leave",
};
